/**
 * ListingAgreementPreviewButton — "Preview / print agreement" (QA T-45).
 *
 * Before signing, the seller can open the Seller Listing Agreement as a PDF
 * to print or save. It is the unsigned document DocuSign receives (GET
 * /api/houses/{id}/listing-agreement/preview-pdf), not the in-page preview,
 * so what they print is what they sign.
 *
 * Like the condition-report button, the PDF is fetched with the session
 * cookie and shown from an object URL: a browser navigation to the API
 * can't carry the cookie auth through the dev proxy.
 */

import React, { useEffect, useRef, useState } from 'react';
import APIAgent from '../../agents/apiAgent';
import styles from './ListingAgreementPreviewButton.module.css';

export const PREVIEW_LABEL = 'Preview / print agreement';

export const PREVIEW_HINT =
  "Opens the agreement you'll sign in DocuSign as a PDF, so you can print or save it first.";

/** How long an opened agreement's object URL is kept before it is released. */
const OBJECT_URL_TTL_MS = 60000;

function errorMessageFor(err) {
  const status = err?.status ?? err?.statusCode;
  if (status === 401) return 'Your session has ended. Sign in again to view the agreement.';
  if (status === 403) return "You don't have access to this agreement.";
  if (status === 404) return "This listing's agreement isn't available.";
  if (status === 503) return "The agreement PDF can't be generated right now. Please try again shortly.";
  return err?.message || "We couldn't open the agreement. Please try again.";
}

/**
 * @param {object} props
 * @param {number} props.houseId - listing the agreement binds to; nothing
 *   renders until it is known
 * @param {string} [props.buttonClassName] - the host page's button style
 * @param {string|null} [props.hint] - line under the button; null for none
 */
export default function ListingAgreementPreviewButton({
  houseId,
  buttonClassName,
  hint = PREVIEW_HINT,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const urlsRef = useRef([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      urlsRef.current = [];
    };
  }, []);

  if (houseId == null) return null;

  const openAgreement = async () => {
    if (loading) return;
    setError(null);
    setLoading(true);
    // Opened now, while the click still counts as a user gesture; popup
    // blockers refuse a window opened after the fetch.
    const tab = typeof window.open === 'function' ? window.open('', '_blank') : null;
    if (tab) {
      try {
        tab.opener = null;
      } catch (_) {
        /* some browsers make opener read-only */
      }
    }
    try {
      const { blob } = await APIAgent.getListingAgreementPreviewPdf(houseId);
      const pdf = blob.type ? blob : new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdf);
      urlsRef.current.push(url);
      setTimeout(() => {
        URL.revokeObjectURL(url);
        urlsRef.current = urlsRef.current.filter((u) => u !== url);
      }, OBJECT_URL_TTL_MS);
      if (tab) {
        tab.location.href = url;
      } else {
        // Popup blocked: a link click opens it in a tab instead.
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      if (tab) tab.close();
      console.error('Listing agreement preview PDF fetch failed:', err?.status);
      if (mountedRef.current) setError(errorMessageFor(err));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  return (
    <div className={styles.wrap} data-testid="agreement-print">
      <button
        type="button"
        className={buttonClassName}
        onClick={openAgreement}
        disabled={loading}
        aria-busy={loading || undefined}
        data-testid="agreement-print-btn"
      >
        {loading ? 'Opening agreement…' : PREVIEW_LABEL}
      </button>
      {hint && <p className={styles.hint}>{hint}</p>}
      {error && (
        <p className={styles.error} role="alert" data-testid="agreement-print-error">
          {error}
        </p>
      )}
    </div>
  );
}
